import { z } from 'zod';

import { LosId, PaymentConfigId, PrincipalModificationId } from '../../BrandedIds';
import { type PaymentConfigSource } from '../../loan/PaymentConfig';
import { zodTemporalInstant, zodTemporalPlainDate } from '../../Temporal';
import { literalsCovering } from '../util/literalsCovering';

const PAYMENT_CONFIG_SOURCES = literalsCovering<PaymentConfigSource>()({
  current: [
    'initial',
    'escrowMonthlyInsuranceManualUpdate',
    'updateBuydownInterestRate',
    'armUpdate',
    'armManualUpdate',
    'constructionToPermanentEvent',
    'updateDelinquentInterestRate',
    'manualUpdate',
    'drawRecord',
    'flexModification',
    'principalModification',
  ],
});

/**
 * One entry of a loan's payment-config history. The entry with the greatest
 * `effectiveDate` at or before a target date (in `ordinal` order) is the config in
 * effect on that date; entries with `isInvalid` set should be skipped.
 */
export const ExportablePaymentConfig = z
  .object({
    id: PaymentConfigId,
    loanId: LosId,
    ordinal: z
      .int()
      .describe(
        "Position of this entry in the loan's payment-config history. Order is authoritative where effective dates tie.",
      ),

    source: z.enum(PAYMENT_CONFIG_SOURCES),
    createdAt: zodTemporalInstant,
    effectiveDate: zodTemporalPlainDate,
    type: z.enum(['fixed', 'arm']),
    isInterestOnly: z.boolean(),
    monthlyPrincipalAndInterestTotal: z
      .number()
      .describe('Combined monthly principal + interest amount for this config period.'),
    monthlyInterestRate: z
      .number()
      .describe('Misnamed upstream: this is the ANNUAL interest rate, as a decimal (e.g. 0.065 for 6.5%).'),
    modificationId: PrincipalModificationId.optional(),
    isInvalid: z.boolean().optional(),
  })
  .meta({ title: 'PaymentConfig' });
export type ExportablePaymentConfig = z.infer<typeof ExportablePaymentConfig>;
